export const COLORS = {
  CARD: '#FFFFFF',
  CARD_OPEN: '#F7F3F1',
  STROKE: '#BCAFA7',
  TEXT: 'rgba(41,30,23,0.92)',
  DOT: '#2D2018',
  PILL_BORDER: '#D9CEC7',
};

export const CATEGORIES = [
  'Getting Started',
  'Qualifications & Standards',
  'Sessions & Process',
  'Effectiveness & Research',
  'Pricing & Insurance',
];

export const FAQ_BY_CATEGORY = {
  'Getting Started': [
    {
      q: 'What is hypnosis?',
      a: 'A focused, relaxed state where attention narrows and learning becomes easier. You remain aware, in control, and able to speak or stop at any time.',
    },
    {
      q: 'What is hypnotherapy?',
      a: 'A structured use of hypnosis plus change methods (e.g., cognitive strategies, guided imagery, hypnotic rehearsal) to help you reach specific goals.',
    },
    {
      q: 'Is hypnosis mind control?',
      a: "No. You can't be made to do anything against your values. It's a cooperative process that relies on your consent and participation.",
    },
    {
      q: 'Will I be unconscious?',
      a: 'No. Most people feel calm, absorbed, and aware—similar to being immersed in a book or music.',
    },
    {
      q: 'What does it feel like?',
      a: "Comfortable and focused. Some feel light and spacious; others simply feel quietly attentive. You're always able to communicate.",
    },
    {
      q: 'Can anyone be hypnotized?',
      a: 'Most people can, to varying degrees. Motivation, rapport, and clear goals are bigger predictors of success than "hypnotizability."',
    },
    {
      q: 'Do I have to visualize well for hypnosis to work?',
      a: 'No. Hypnosis uses multiple senses and styles (words, imagery, sensation, memory, symbols). Your practitioner adapts to how you process best.',
    },
    {
      q: "What's the difference between stage hypnosis and clinical hypnotherapy?",
      a: 'Stage hypnosis is entertainment. Clinical hypnotherapy is a structured, goal-directed service focused on personal change.',
    },
    {
      q: "What if I'm nervous about hypnosis?",
      a: "Totally normal. We'll go step-by-step, set boundaries, and you can pause any time. Comfort first; change follows.",
    },
  ],
  'Qualifications & Standards': [
    {
      q: 'Does hypnotherapy replace medical or counseling care?',
      a: "No. It's not a substitute. We work alongside your licensed clinician when appropriate and stay within scope.",
    },
    {
      q: 'Is hypnotherapy appropriate for trauma-related goals?',
      a: 'Often as an adjunct with stabilization, pacing, and coordination with your primary clinician. Your safety and choice lead the process.',
    },
    {
      q: "Are there situations where hypnosis isn't a good fit?",
      a: "Yes—certain conditions, medications, or acute states may call for different support first. We'll screen during intake and provide referrals or coordination as needed.",
    },
    {
      q: 'Are practitioners certified?',
      a: 'Yes. Sessions are led by IAIH-certified hypnotherapy practitioners with advanced training in ethical, client-centered methods.',
    },
    {
      q: 'Can minors work with you?',
      a: 'Yes, with guardian consent and an approach tailored to attention span and goals.',
    },
    {
      q: 'Is it safe?',
      a: 'For most people, yes—when delivered by trained practitioners in appropriate situations. We use informed consent, scope screening, and privacy safeguards.',
    },
    {
      q: 'Is it confidential?',
      a: 'Yes. We use HIPAA-compliant systems, protect your information, and only coordinate with others with your written permission.',
    },
  ],
  'Sessions & Process': [
    {
      q: 'How many sessions will I need?',
      a: 'Goals vary, but many clients notice meaningful progress in 3–6 sessions. We review results and adjust your plan together.',
    },
    {
      q: 'How long is a session?',
      a: 'Typically 60–120 minutes. Each clock hour includes 50 minutes of dedicated session time within a 60-minute scheduling block, allowing time for notes and transitions.',
    },
    {
      q: 'What happens in a session?',
      a: [
        'Brief check-in and goal review',
        'Gentle hypnotic induction',
        'Change work (e.g., suggestion, parts work, cognitive-behavioral hypnotherapy, imagery)',
        'Practical skills (often self-hypnosis) and next steps',
      ],
    },
    {
      q: 'Do you assign "home practice"?',
      a: 'Yes—simple skills you can use in a few minutes a day (self-hypnosis audio, calming cues, micro-habits). This accelerates progress.',
    },
    {
      q: 'Will I remember what happened?',
      a: 'Usually yes. Some people experience light, dream-like moments; you can request summaries or session notes.',
    },
    {
      q: 'Can I get "stuck" in hypnosis?',
      a: 'No. If a connection drops online, you simply open your eyes, re-orient, and we reconnect.',
    },
    {
      q: 'What technology do I need?',
      a: 'A private space, stable internet, a computer or tablet (headphones recommended), and a comfortable chair. Please silence notifications.',
    },
    {
      q: 'Do you record sessions?',
      a: "By default, no. If we provide an audio (e.g., self-hypnosis practice), we'll share it securely with your consent.",
    },
    {
      q: 'Do you offer regression work?',
      a: 'Our focus is forward-moving change. Regression-style work is optional, considered case-by-case, and only if it clearly supports your goals and consent.',
    },
    {
      q: 'Will I get a customized plan?',
      a: 'Yes. We co-create clear goals, session focus, and simple practices that fit your life.',
    },
    {
      q: 'How should I prepare?',
      a: 'Be rested, hydrated, and in a quiet space. Bring 1–2 clear goals. Arrive a few minutes early to settle and check tech.',
    },
    {
      q: 'What increases results?',
      a: "Clarity of goals, brief daily practice, and honest feedback. We'll adapt the plan quickly based on what helps you most.",
    },
  ],
  'Effectiveness & Research': [
    {
      q: 'Is hypnotherapy evidence-informed?',
      a: 'Yes. Hypnosis has research support across areas like stress, sleep, comfort skills for pain, IBS protocols, and behavior change. We use methods aligned with that literature while personalizing to you.',
    },
    {
      q: 'Is online hypnotherapy effective?',
      a: 'Yes. Many people prefer the comfort of home. We use secure video and clear audio for a steady experience.',
    },
    {
      q: 'Can hypnotherapy help with habits like smoking or nail biting?',
      a: 'Yes—by reducing triggers, strengthening identity-level change, and rehearsing new responses. We pair hypnosis with practical between-session tools.',
    },
    {
      q: 'Can it support sleep issues?',
      a: "Often—by easing mental overactivity and installing wind-down routines. We'll give you simple practices to use at night.",
    },
    {
      q: 'Can it support comfort for persistent pain?',
      a: 'Hypnosis can be helpful for comfort and coping as an adjunct to your care plan. We coordinate with your clinician on request.',
    },
  ],
  'Pricing & Insurance': [
    {
      q: 'Do you take insurance?',
      a: 'We can provide receipts for out-of-network wellness reimbursement if your plan allows; coverage varies. Many clients use HSA/FSA.',
    },
    {
      q: 'What is your cancellation policy?',
      a: "Your appointment time is reserved for you. Please provide 24–48 hours' notice per our policy to avoid a late fee.",
    },
  ],
};
